import { formatBytes, formatDate, type Media, type MediaKind } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Film, ImageIcon, PenLine, Trash2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Badge, Card, EmptyState, Spinner } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Select } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { KIND_KEYS, MEDIA_STATUS_KEYS, MEDIA_STATUS_TONE } from "@/lib/labels";
import { detectKind, uploadFile } from "@/lib/upload";

import { usePropertyContext } from "./PropertyDetailPage";

interface QueueItem {
  key: string;
  name: string;
  size: number;
  loaded: number;
  state: "uploading" | "done" | "error";
  error?: string;
}

const IN_FLIGHT = ["pending", "uploaded", "processing"];

export function MediaTab() {
  const { property } = usePropertyContext();
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const wallFilter = sp.get("wall") ?? "";
  const kindFilter = sp.get("kind") ?? "";
  const fileInput = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [uploadKind, setUploadKind] = useState<"auto" | MediaKind>("auto");
  const [del, setDel] = useState<Media | null>(null);

  const tree = useQuery({
    queryKey: ["property-tree", property.id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/properties/{id}/tree/", { params: { path: { id: property.id } } }),
      ),
  });
  const walls = (tree.data?.rooms ?? []).flatMap((r) =>
    r.walls.map((w) => ({ id: w.id, roomId: r.id, label: `${r.name} / ${w.name}` })),
  );

  const media = useQuery({
    queryKey: ["media", property.id, { wallFilter, kindFilter }],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/media/", {
          params: {
            query: {
              property: property.id,
              wall: wallFilter || undefined,
              kind: kindFilter || undefined,
              page_size: 100,
            },
          },
        }),
      ),
    // Qayta ishlanayotgan fayllar tayyor bo'lguncha vaqti-vaqti bilan so'raymiz
    refetchInterval: (q) =>
      q.state.data?.results.some((m) => IN_FLIGHT.includes(m.status ?? "")) ? 3000 : false,
  });

  const patchQueue = (key: string, patch: Partial<QueueItem>) =>
    setQueue((q) => q.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    const wall = walls.find((w) => w.id === wallFilter);
    for (const file of Array.from(files)) {
      const kind = uploadKind === "auto" ? detectKind(file) : uploadKind;
      const key = `${file.name}:${file.size}:${++seq.current}`;
      if (!kind) {
        setQueue((q) => [
          ...q,
          {
            key,
            name: file.name,
            size: file.size,
            loaded: 0,
            state: "error",
            error: t("media.unsupported"),
          },
        ]);
        continue;
      }
      setQueue((q) => [
        ...q,
        { key, name: file.name, size: file.size, loaded: 0, state: "uploading" },
      ]);
      try {
        await uploadFile(
          api,
          file,
          { property: property.id, room: wall?.roomId ?? null, wall: wall?.id ?? null, kind },
          { onProgress: (loaded) => patchQueue(key, { loaded }) },
        );
        patchQueue(key, { state: "done", loaded: file.size });
        void qc.invalidateQueries({ queryKey: ["media", property.id] });
      } catch (e) {
        patchQueue(key, { state: "error", error: errorMessage(e, t("common.error")) });
      }
    }
    if (fileInput.current) fileInput.current.value = "";
  }

  const remove = useMutation({
    mutationFn: async (m: Media) => {
      const res = await api.DELETE("/api/media/{id}/", { params: { path: { id: m.id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      setDel(null);
      void qc.invalidateQueries({ queryKey: ["media", property.id] });
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  const reprocess = useMutation({
    mutationFn: async (m: Media) => {
      const res = await api.POST("/api/media/{id}/reprocess/", { params: { path: { id: m.id } } });
      if (!res.response.ok) throw new Error("reprocess");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["media", property.id] }),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  const setFilter = (k: string, v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v);
    else next.delete(k);
    setSp(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            aria-label={t("media.filter_wall")}
            value={wallFilter}
            onChange={(e) => setFilter("wall", e.target.value)}
          >
            <option value="">{t("media.all_walls")}</option>
            {walls.map((w) => (
              <option key={w.id} value={w.id}>
                {w.label}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t("media.filter_kind")}
            value={kindFilter}
            onChange={(e) => setFilter("kind", e.target.value)}
          >
            <option value="">{t("media.all_kinds")}</option>
            {(Object.keys(KIND_KEYS) as MediaKind[]).map((k) => (
              <option key={k} value={k}>
                {t(KIND_KEYS[k])}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t("media.upload_as")}
            value={uploadKind}
            onChange={(e) => setUploadKind(e.target.value as "auto" | MediaKind)}
          >
            <option value="auto">{t("media.kind_auto")}</option>
            {(Object.keys(KIND_KEYS) as MediaKind[]).map((k) => (
              <option key={k} value={k}>
                {t(KIND_KEYS[k])}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            accept="image/*,video/*,application/pdf,.heic,.heif"
            onChange={(e) => void onFiles(e.target.files)}
            data-testid="file-input"
          />
          <Button onClick={() => fileInput.current?.click()}>
            <Upload className="size-4" />{" "}
            {wallFilter ? t("media.upload_to_wall") : t("media.upload")}
          </Button>
          <span className="text-sm text-muted-foreground">{t("media.hint")}</span>
        </div>

        {queue.length > 0 && (
          <ul className="flex flex-col gap-2" aria-label={t("media.queue")}>
            {queue.map((i) => (
              <li key={i.key} className="rounded-lg border border-border p-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{i.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatBytes(i.size)}
                  </span>
                  <button
                    aria-label={t("common.close")}
                    className="shrink-0 text-muted-foreground"
                    onClick={() => setQueue((q) => q.filter((x) => x.key !== i.key))}
                  >
                    <X className="size-4" />
                  </button>
                </div>
                {i.state === "uploading" && (
                  <div
                    className="mt-1 h-2 overflow-hidden rounded bg-muted"
                    role="progressbar"
                    aria-valuenow={Math.round((i.loaded / i.size) * 100)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${(i.loaded / i.size) * 100}%` }}
                    />
                  </div>
                )}
                {i.state === "done" && (
                  <div className="mt-1 text-xs text-success">{t("media.uploaded")}</div>
                )}
                {i.state === "error" && <div className="mt-1 text-xs text-danger">{i.error}</div>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {media.isLoading && <Spinner className="m-8 size-8" />}
      {media.data && media.data.results.length === 0 && <EmptyState>{t("media.empty")}</EmptyState>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {media.data?.results.map((m) => {
          const kind = (m.kind ?? "photo") as MediaKind;
          const status = m.status ?? "pending";
          return (
            <Card key={m.id} className="flex flex-col gap-2 p-2">
              <a
                href={m.file_url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={m.filename}
                className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg bg-muted"
              >
                {m.thumb_url ? (
                  <img
                    src={m.thumb_url}
                    alt={m.note || m.filename}
                    loading="lazy"
                    className="size-full object-cover"
                  />
                ) : status === "ready" && kind === "video" ? (
                  <Film className="size-8 text-muted-foreground" />
                ) : status === "ready" ? (
                  <FileText className="size-8 text-muted-foreground" />
                ) : (
                  <ImageIcon className="size-8 text-muted-foreground" />
                )}
                {kind === "video" && m.thumb_url && (
                  <Film className="absolute right-2 top-2 size-5 text-white drop-shadow" />
                )}
              </a>
              <div className="flex items-center justify-between gap-1">
                <Badge tone={MEDIA_STATUS_TONE[status]}>{t(MEDIA_STATUS_KEYS[status])}</Badge>
                <span className="text-xs text-muted-foreground">{t(KIND_KEYS[kind])}</span>
              </div>
              {m.error && <p className="text-xs text-danger">{m.error}</p>}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatDate(m.taken_at ?? m.created_at)}</span>
                <span>{m.file_size ? formatBytes(m.file_size) : ""}</span>
              </div>
              <div className="flex gap-1">
                {status === "ready" && (kind === "photo" || kind === "scheme") && m.thumb_url && (
                  <Button asChild size="sm" variant={m.has_annotation ? "outline" : "ghost"}>
                    <Link
                      to={`/properties/${property.id}/annotate/${m.id}`}
                      aria-label={`${t("media.annotate")}: ${m.filename}`}
                    >
                      <PenLine className="size-4" />{" "}
                      {m.has_annotation ? t("media.annotated") : t("media.annotate")}
                    </Link>
                  </Button>
                )}
                {status === "failed" && (
                  <Button size="sm" variant="outline" onClick={() => reprocess.mutate(m)}>
                    {t("media.retry")}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  aria-label={t("common.delete")}
                  onClick={() => setDel(m)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title={t("media.delete_title")}
        message={t("media.delete_message")}
        confirmLabel={t("common.delete")}
        pending={remove.isPending}
        onConfirm={() => del && remove.mutate(del)}
      />
    </div>
  );
}
